/**
 * Dashboard Page - Tab-Style Quick Actions with Forest Green Theme
 * Clean design matching your brand colors
 */

import React, { useState, useEffect } from 'react';
import { DashboardStats } from '@components/dashboard/dashboard_stats';
import { RevenueChart } from '@components/dashboard/revenue_chart';
import { ConsumptionChart } from '@components/dashboard/consumption_chart';
import { PageLoading } from '@components/common/loading_spinner';
import { Button } from '@components/common/button';
import { Card } from '@components/common/card';
import { toast } from 'react-toastify';
import {
  DashboardStats as DashboardStatsType,
  RecentActivity as RecentActivityType,
  RevenueData,
  ConsumptionData,
  DashboardFilters,
  // ConsumptionPeriod
} from '@/types/dashboard_types';
import {
  fetch_dashboard_stats,
  fetch_recent_activity,
  fetch_revenue_data,
  fetch_consumption_data
} from '@/services/dashboard_service';

import { 
  RefreshCw, 
  Calendar, 
  AlertCircle, 
  TrendingUp,
  UserPlus,
  Droplets,
  FileText,
  DollarSign,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();

  // State management
  const [stats, set_stats] = useState<DashboardStatsType | null>(null);
  const [, set_activities] = useState<RecentActivityType[]>([]);
  const [revenue_data, set_revenue_data] = useState<RevenueData[]>([]);
  const [consumption_data, set_consumption_data] = useState<ConsumptionData[]>([]);
  const [is_loading, set_is_loading] = useState(true);
  const [is_refreshing, set_is_refreshing] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  const [filters, set_filters] = useState<DashboardFilters>({
    period: 'month'
  });

  useEffect(() => {
    load_dashboard_data();
  }, [filters]);

  const load_dashboard_data = async () => {
    try {
      set_is_loading(true);
      set_error(null);

      const [
        stats_data,
        activity_data,
        revenue_chart_data,
        consumption_chart_data
      ] = await Promise.all([
        fetch_dashboard_stats(),
        fetch_recent_activity({ limit: 20 }),
        fetch_revenue_data(filters), 
        fetch_consumption_data({ period: 'year' })
      ]);

      set_stats(stats_data);
      set_activities(activity_data);
      
      const transformed_revenue_data = revenue_chart_data.map(item => ({
        ...item,
        invoiced: item.total_revenue || item.invoices_generated || item.invoiced,
        collected: item.payments_received || item.collected
      }));
      
      set_revenue_data(transformed_revenue_data);
      set_consumption_data(consumption_chart_data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      set_error('Failed to load dashboard data. Please try again.');
      toast.error('Failed to load dashboard data');
    } finally {
      set_is_loading(false);
    }
  };

  const handle_refresh = async () => {
    try {
      set_is_refreshing(true);
      await load_dashboard_data();
      toast.success('Dashboard refreshed successfully');
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
      toast.error('Failed to refresh dashboard');
    } finally {
      set_is_refreshing(false);
    }
  };

  const handle_period_change = (period: DashboardFilters['period']) => {
    set_filters(prev => ({ ...prev, period }));
  };

  const get_period_label = (period: DashboardFilters['period']) => {
    const labels = {
      week: 'Last 7 Days',
      month: 'Current Month',
      quarter: 'Last 3 Months',
      year: 'Last 12 Months'
    };
    return labels[period];
  };

  // Tab-style Quick Actions
  const quick_actions = [
    {
      title: 'Add Client',
      icon: <UserPlus size={16} />,
      action: () => navigate('/clients'),
      primary: true, // First one is primary with forest green
    },
    {
      title: 'Record Reading',
      icon: <Droplets size={16} />,
      action: () => navigate('/readings'),
      primary: false,
    },
    {
      title: 'Create Invoice',
      icon: <FileText size={16} />,
      action: () => navigate('/invoices'),
      primary: false,
    },
    {
      title: 'Record Payment',
      icon: <DollarSign size={16} />,
      action: () => navigate('/payments'),
      primary: false,
    },
  ];

  if (is_loading) {
    return <PageLoading message="Loading dashboard..." />;
  }

  if (error || !stats) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto">
          <Card padding="lg">
            <div className="text-center py-12">
              <AlertCircle size={64} className="mx-auto text-error mb-4" />
              <h2 className="text-2xl font-bold text-charcoal mb-2">
                Failed to Load Dashboard
              </h2>
              <p className="text-gray-600 mb-6">
                {error || 'Unable to load dashboard data. Please check your connection and try again.'}
              </p>
              <Button
                variant="primary"
                onClick={load_dashboard_data}
                icon_left={<RefreshCw size={18} />}
              >
                Retry
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-charcoal">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Welcome back! Here's an overview of your water management system.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Period Filter */}
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-300 shadow-sm">
            <Calendar size={16} className="text-gray-500" />
            <select
              value={filters.period}
              onChange={(e) => handle_period_change(e.target.value as DashboardFilters['period'])}
              className="text-sm border-none focus:outline-none cursor-pointer bg-transparent"
            >
              <option value="week">Last 7 Days</option>
              <option value="month">Current Month</option>
              <option value="quarter">Last 3 Months</option>
              <option value="year">Last 12 Months</option>
            </select>
          </div>

          {/* Refresh Button */}
          <Button
            variant="outline"
            size="md"
            onClick={handle_refresh}
            is_loading={is_refreshing}
            icon_left={<RefreshCw size={18} />}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Tab-Style Quick Actions - Forest Green Theme */}
      <div className="bg-white rounded-lg border border-light-gray shadow-sm">
        <div className="flex items-center gap-1 p-2">
          {quick_actions.map((action, index) => (
            action.primary ? (
              // Primary button - filled forest green with icon
              <button
                key={index}
                onClick={action.action}
                className="inline-flex items-center gap-2 px-4 py-2 bg-forest-green hover:bg-dark-forest text-white text-sm font-medium rounded-md transition-colors duration-150"
              >
                {action.icon}
                <span>{action.title}</span>
              </button>
            ) : (
              // Secondary buttons - subtle with icon
              <button
                key={index}
                onClick={action.action}
                className="inline-flex items-center gap-2 px-4 py-2 text-medium-gray hover:text-charcoal hover:bg-off-white text-sm font-medium rounded-md transition-colors duration-150"
              >
                {action.icon}
                <span>{action.title}</span>
              </button>
            )
          ))}
        </div>
      </div>

      {/* Alert for Issues Requiring Attention */}
      {(stats.meters.faulty > 0 || stats.meters.needs_reading > 0 || stats.invoices.overdue > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.meters.faulty > 0 && (
            <div className="p-4 bg-error bg-opacity-10 border border-error rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-error flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-error mb-1">
                    {stats.meters.faulty} Faulty Meter{stats.meters.faulty > 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-error">
                    Requires immediate attention
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/meters')}
                  className="text-error"
                >
                  View
                </Button>
              </div>
            </div>
          )}

          {stats.meters.needs_reading > 0 && (
            <div className="p-4 bg-warning bg-opacity-10 border border-warning rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-warning flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-warning mb-1">
                    {stats.meters.needs_reading} Meter{stats.meters.needs_reading > 1 ? 's' : ''} Need Reading
                  </p>
                  <p className="text-xs text-warning">
                    Readings overdue by 30+ days
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/readings')}
                  className="text-warning"
                >
                  View
                </Button>
              </div>
            </div>
          )}

          {stats.invoices.overdue > 0 && (
            <div className="p-4 bg-error bg-opacity-10 border border-error rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-error flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-error mb-1">
                    {stats.invoices.overdue} Overdue Invoice{stats.invoices.overdue > 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-error">
                    Follow up required
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/invoices')}
                  className="text-error"
                >
                  View
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Stats */}
      <DashboardStats stats={stats} />

      {/* Charts Row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <RevenueChart data={revenue_data} />
        <ConsumptionChart data={consumption_data} />
      </div>

      {/* Period Info */}
      <div className="text-center text-sm text-gray-500">
        <p className="flex items-center justify-center gap-2">
          <TrendingUp size={14} />
          Showing data for: {get_period_label(filters.period)}
        </p>
      </div>
    </div>
  );
};